/**
 * SubscriptionPanel - Subscription management for Settings dialog
 * Shows current plan, trial status, upgrade/manage actions
 */

import { FC } from 'react'
import { CreditCard, Sparkles, Clock, Crown, ExternalLink } from 'lucide-react'
import { Button } from '../ui/button'
import { useSubscription } from '../../contexts/SubscriptionContext'
import { cn } from '../../lib/utils'

export const SubscriptionPanel: FC = () => {
  const {
    subscription,
    loading,
    isActive,
    isTrialing,
    isOverride,
    daysRemaining,
    statusMessage,
    upgrade,
    manageSubscription
  } = useSubscription()

  if (loading || !subscription) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-48 bg-muted rounded" />
          <div className="h-32 bg-muted rounded-xl" />
        </div>
      </div>
    )
  }

  // Determine plan display info
  const getPlanInfo = () => {
    if (isOverride) {
      return { name: 'Premium Access', icon: Crown, bgClass: 'bg-clinical-teal/10', textClass: 'text-clinical-teal' }
    }
    if (subscription.status === 'active') {
      return { name: 'Pro', icon: Sparkles, bgClass: 'bg-clinical-teal/10', textClass: 'text-clinical-teal' }
    }
    if (isTrialing) {
      return { name: 'Trial', icon: Clock, bgClass: 'bg-clinical-cyan/10', textClass: 'text-clinical-cyan' }
    }
    return { name: 'Free', icon: CreditCard, bgClass: 'bg-muted/10', textClass: 'text-muted-foreground' }
  }

  const planInfo = getPlanInfo()
  const PlanIcon = planInfo.icon

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-clinical-teal/20 to-clinical-cyan/10 flex items-center justify-center">
          <CreditCard className="h-4 w-4 text-clinical-teal" />
        </div>
        <div>
          <h2 className="text-base font-display font-semibold">Subscription</h2>
          <p className="text-xs text-muted-foreground">Manage your plan</p>
        </div>
      </div>

      {/* Current Plan Card */}
      <div className="rounded-xl border border-border/50 bg-surface/50 p-5 mb-4">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={cn(
              'w-10 h-10 rounded-xl flex items-center justify-center',
              planInfo.bgClass
            )}>
              <PlanIcon className={cn('h-5 w-5', planInfo.textClass)} />
            </div>
            <div>
              <h3 className="text-lg font-semibold">{planInfo.name}</h3>
              <p className="text-sm text-muted-foreground">{statusMessage}</p>
            </div>
          </div>
          {isActive && !isOverride && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-clinical-sage/10 text-clinical-sage text-xs font-medium">
              <span className="w-1.5 h-1.5 rounded-full bg-current" />
              Active
            </span>
          )}
        </div>

        {/* Trial countdown */}
        {isTrialing && daysRemaining !== null && (
          <div className={cn(
            'rounded-lg p-3 mb-4',
            daysRemaining <= 3 ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-muted/50'
          )}>
            <div className="flex items-center gap-2">
              <Clock className={cn('h-4 w-4', daysRemaining <= 3 ? 'text-amber-500' : 'text-muted-foreground')} />
              <span className={cn('text-sm font-medium', daysRemaining <= 3 ? 'text-amber-500' : 'text-foreground')}>
                {daysRemaining === 0 ? 'Trial ends today' :
                 daysRemaining === 1 ? '1 day remaining' :
                 `${daysRemaining} days remaining`}
              </span>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3">
          {subscription.can_upgrade && (
            <Button
              onClick={upgrade}
              className="flex-1 bg-gradient-to-r from-clinical-teal to-clinical-sage hover:from-clinical-teal/90 hover:to-clinical-sage/90 text-white"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Upgrade to Pro
            </Button>
          )}
          {subscription.status === 'active' && !isOverride && (
            <Button
              onClick={manageSubscription}
              variant="outline"
              className="flex-1"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Manage Subscription
            </Button>
          )}
        </div>
      </div>

      {/* Pro Features */}
      <div className="rounded-xl border border-border/50 bg-surface/50 p-5">
        <h4 className="text-sm font-semibold mb-3">Pro Features</h4>
        <ul className="space-y-2.5">
          {[
            'Unlimited AI conversations',
            'Advanced clinical document generation',
            'Priority support',
            'Extended conversation history',
            'Custom templates'
          ].map((feature) => (
            <li key={feature} className="flex items-center gap-2 text-sm text-muted-foreground">
              <span className="w-1.5 h-1.5 rounded-full bg-clinical-teal" />
              {feature}
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}
